/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactNode } from 'react';

import { Drawer } from '../../Overlays';
import {
  CommandMenuProvider,
  CommandMenuInput,
  CommandMenuItem,
  CommandMenuList,
  CommandMenuEmpty,
  CommandMenuGroup,
} from '../../CommandMenu';

import { normalizeText } from 'utils/text';
import { withId } from 'utils/id';

import { SelectOption } from './types';
import { ensureArray } from 'utils/array';

type SelectOptionGroup = {
  options: SelectOption[];
  label: ReactNode;
};

interface MobileSelectProps {
  /**
   * The select elem
   */
  children: ReactNode;

  isOpen: boolean;
  close: () => void;

  isSearchable: boolean;
  isMulti?: boolean;
  isGrouped: boolean;

  formatLabel?: (entity: any) => ReactNode;
  onChange: (value: SelectOption | SelectOption[]) => void;
  value?: SelectOption | SelectOption[];

  options: SelectOption[] | SelectOptionGroup[];

  title?: ReactNode;
}

export function MobileSelectWrap({
  children,
  close,
  isOpen,
  isSearchable,
  formatLabel,
  onChange,
  isMulti,
  isGrouped,
  options,
  value,
  title,
}: MobileSelectProps) {
  const groups = withId(
    isGrouped
      ? (options as SelectOptionGroup[])
      : [{ label: '', options: options as SelectOption[] }],
  );

  const selectedValues = ensureArray(value ?? []).map((o) => o.value);

  const handleChange = (selectedOption: SelectOption) => {
    if (!isMulti) {
      close();
      return onChange(selectedOption);
    }

    const valueArray = (value as SelectOption[]) || [];
    const isRemoval = valueArray.find((o) => o.value === selectedOption.value);

    const removedList = valueArray.filter((o) => o.value !== selectedOption.value);

    onChange(isRemoval ? removedList : [...removedList, selectedOption]);
  };

  return (
    <>
      <Drawer isOpen={isOpen} close={close} className="p-0 phone:px-0" gap="1">
        {title && (
          <p className="flex items-center gap-2 pl-2 text-lg font-semibold leading-none tracking-tight">
            {title}
          </p>
        )}

        <CommandMenuProvider className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          {isSearchable && (
            <CommandMenuInput placeholder={`Search...`} containerClassName="border-b-0" />
          )}

          <CommandMenuList className="min-h-44">
            <CommandMenuEmpty>{'No options...'}</CommandMenuEmpty>

            {groups.map(({ label, options, id }) => (
              <CommandMenuGroup key={id} heading={label}>
                {options.map((selectOption) => (
                  <CommandMenuItem
                    key={selectOption.value}
                    value={normalizeText(selectOption.label)}
                    disabled={selectOption.isDisabled}
                    onSelect={() => handleChange(selectOption)}
                    className={selectedValues.includes(selectOption.value) ? '!text-primary' : ''}
                  >
                    {formatLabel ? formatLabel?.(selectOption.entity) : selectOption.label}
                  </CommandMenuItem>
                ))}
              </CommandMenuGroup>
            ))}
          </CommandMenuList>
        </CommandMenuProvider>
      </Drawer>

      {children}
    </>
  );
}
